import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  requestNotificationPermission,
  onMessageListener,
  getNotificationPermission,
  isNotificationSupported
} from '../config/firebase';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [fcmToken, setFcmToken] = useState(null);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [isSupported, setIsSupported] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Check notification support on mount
  useEffect(() => {
    setIsSupported(isNotificationSupported());
    setNotificationPermission(getNotificationPermission());
  }, []);

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/firebase-messaging-sw.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }
  }, []);

  // Listen for foreground messages
  useEffect(() => {
    const unsubscribe = onMessageListener((payload) => {
      console.log('Foreground notification received:', payload);

      // 1. Show In-App Toast (Visual Popup)
      if (payload.notification) {
        toast.info(
          <div>
            <h4 style={{ fontWeight: 'bold', margin: 0 }}>{payload.notification.title}</h4>
            <p style={{ margin: 0, fontSize: '0.9em' }}>{payload.notification.body}</p>
          </div>,
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true
          }
        );

        // 2. Show System Notification (Native "Phone Style" Popup)
        // This ensures it pops up even if the user is looking at the screen, 
        // mimicking the native OS feel they requested.
        if ('Notification' in window && Notification.permission === 'granted') {
          try {
            new Notification(payload.notification.title, {
              body: payload.notification.body,
              icon: '/l2t-logo.png', // Corrected path to existing logo
              data: payload.data
            });
          } catch (error) {
            console.error('System notification error:', error);
          }
        }
      }

      // Add notification to internal state list
      const newNotification = {
        id: Date.now(),
        title: payload.notification?.title || 'Notification',
        body: payload.notification?.body || '',
        data: payload.data || {},
        timestamp: new Date(),
        read: false
      };

      setNotifications((prev) => [newNotification, ...prev]);
      setUnreadCount((prev) => prev + 1);
    });

    return unsubscribe;
  }, []);

  /**
   * Request notification permission and save FCM token
   */
  const enableNotifications = async () => {
    try {
      console.log('📱 [Frontend] Starting enableNotifications...');

      if (!isSupported) {
        console.error('❌ [Frontend] Notifications not supported');
        throw new Error('Notifications are not supported in this browser');
      }

      if (!user) {
        console.error('❌ [Frontend] User not logged in');
        throw new Error('User must be logged in to enable notifications');
      }

      console.log('📱 [Frontend] User logged in:', user.email);
      console.log('📱 [Frontend] Requesting notification permission...');

      // Request permission and get token
      const token = await requestNotificationPermission();

      console.log('📱 [Frontend] Token received:', token ? 'Yes (' + token.substring(0, 20) + '...)' : 'No');

      if (!token) {
        console.error('❌ [Frontend] Failed to get notification token');
        throw new Error('Failed to get notification token');
      }

      setFcmToken(token);
      setNotificationPermission('granted');
      console.log('✅ [Frontend] FCM token set in state');

      // Save token to backend
      console.log('📱 [Frontend] Calling saveFCMToken...');
      await saveFCMToken(token);
      console.log('✅ [Frontend] Token saved to backend successfully');

      return { success: true, token };
    } catch (error) {
      console.error('❌ [Frontend] Error enabling notifications:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Save FCM token to backend
   */
  const saveFCMToken = async (token) => {
    try {
      const apiPath = '/notifications/fcm-token';
      console.log('📱 [Frontend] Saving token to:', apiPath);
      console.log('📱 [Frontend] Token being sent:', token.substring(0, 20) + '...');

      const response = await axios.post(
        apiPath,
        { token },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      console.log('✅ [Frontend] FCM token save response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Frontend] Error saving FCM token:', error.response?.data || error.message);
      console.error('❌ [Frontend] Full error:', error);
      throw error;
    }
  };

  /**
   * Remove FCM token from backend
   */
  const removeFCMToken = async () => {
    try {
      if (!fcmToken) return;

      await axios.delete(
        `/notifications/fcm-token`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          data: { fcmToken }
        }
      );

      setFcmToken(null);
      console.log('FCM token removed');
    } catch (error) {
      console.error('Error removing FCM token:', error);
      throw error;
    }
  };

  /**
   * Mark notification as read
   */
  const markAsRead = (notificationId) => {
    setNotifications((prev) =>
      prev.map((notif) =>
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  };

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = () => {
    setNotifications((prev) =>
      prev.map((notif) => ({ ...notif, read: true }))
    );
    setUnreadCount(0);
  };

  /**
   * Clear all notifications
   */
  const clearAllNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  /**
   * Get notification permission status
   */
  const checkPermission = () => {
    const permission = getNotificationPermission();
    setNotificationPermission(permission);
    return permission;
  };

  const value = {
    fcmToken,
    notificationPermission,
    isSupported,
    notifications,
    unreadCount,
    enableNotifications,
    removeFCMToken,
    markAsRead,
    markAllAsRead,
    clearAllNotifications,
    checkPermission
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
